/**
 * GenerationFormProvider
 *
 * Provider wrapper that sets up the DataGraph context for the generation form.
 * This includes the graph, storage adapter, and external context.
 */

import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import {
  useGenerationConfig,
  useGenerationStatus,
} from '~/components/ImageGeneration/GenerationForm/generation.utils';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { DataGraphProvider, useDataGraph } from '~/libs/data-graph/react';
import { createLocalStorageAdapter } from '~/libs/data-graph/storage-adapter';
import { generationGraph, type GenerationCtx } from '~/shared/data-graph/generation';
import type { ResourceData, SnippetsNodeValue } from '~/shared/data-graph/generation/common';
import {
  allEcosystemDefaultVersionIds,
  ecosystemByKey,
  ecosystemById,
  getEcosystem,
  areResourcesCompatible,
  filterCompatibleResources,
  getBaseModelsByEcosystemId,
  ecosystemGroups,
  getEcosystemGroup,
  MODEL3D_ECOSYSTEM_KEYS,
} from '~/shared/constants/basemodel.constants';
import { VID_QUANTITY_BY_TIER } from '~/shared/constants/generation.constants';
import {
  workflowConfigByKey,
  isWorkflowAvailable,
  getEcosystemsForWorkflow,
  getOutputTypeForWorkflow,
} from '~/shared/data-graph/generation/config/workflows';
import { splitResourcesByType } from '~/shared/utils/resource.utils';
import {
  useGenerationGraphStore,
  generationGraphStore,
  generationGraphPanel,
} from '~/store/generation-graph.store';
import { workflowPreferences } from '~/store/workflow-preferences.store';

import {
  openCompatibilityConfirmModal,
  buildWorkflowPendingChange,
} from './CompatibilityConfirmModal';
import { useResourceDataContext } from './inputs/ResourceDataProvider';
import { WhatIfProvider } from './WhatIfProvider';
import { needsHydration, type PartialResourceValue } from './inputs/resource-select.utils';
import type { GenerationResource } from '~/shared/types/generation.types';
import { type VersionGroup, getAllVersionIds } from '~/shared/data-graph/generation/common';
import { decodeGenerationHandoff, GENERATION_HANDOFF_PARAM } from './utils/generation-url-handoff';
import { setGenerationSnapshotCache } from './utils/generation-snapshot-cache';
import { ExperimentalRulesSync } from './Experimental';

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'generation-graph';

// =============================================================================
// Storage Utilities
// =============================================================================

/**
 * Clear all localStorage entries for workflows of a given output type.
 * Removes global settings (prompt, seed, etc.), output-scoped ecosystem,
 * workflow-scoped settings, and ecosystem-scoped settings for all
 * ecosystems used by workflows of that output type.
 */
export function clearStorageForOutput(outputType: 'image' | 'video') {
  const remove = (key: string) => storageAdapter.removeKey(key);

  // Global key (prompt, seed, quantity, outputFormat, etc.)
  remove(STORAGE_KEY);

  // Output-scoped ecosystem
  remove(`${STORAGE_KEY}.output.${outputType}`);

  // Collect all ecosystem IDs used by workflows of this output type
  const ecosystemIds = new Set<number>();

  // Workflow-scoped keys
  for (const [key] of workflowConfigByKey) {
    if (getOutputTypeForWorkflow(key) === outputType) {
      remove(`${STORAGE_KEY}.workflow.${key}`);
      for (const ecoId of getEcosystemsForWorkflow(key)) {
        ecosystemIds.add(ecoId);
      }
    }
  }

  // Ecosystem-scoped keys (individual + group)
  const clearedGroups = new Set<string>();
  for (const ecoId of ecosystemIds) {
    const eco = ecosystemById.get(ecoId);
    if (!eco) continue;

    // Individual ecosystem key
    remove(`${STORAGE_KEY}.ecosystem.${eco.key}`);

    // Ecosystem group key (shared settings across group variants)
    const group = getEcosystemGroup(ecoId);
    if (group && !clearedGroups.has(group.id)) {
      remove(`${STORAGE_KEY}.ecosystem.${group.id}`);
      clearedGroups.add(group.id);
    }
  }
}

/**
 * Returns all model version IDs to prefetch for the compatibility modal:
 * ecosystem defaults + last-used checkpoints for every ecosystem in localStorage.
 *
 * Both GenerationTabs (prefetch) and the modal use this function so their
 * query keys always match, guaranteeing an instant cache hit when the modal opens.
 */
export function getAllEcosystemVersionIdsForPrefetch(): number[] {
  const ids = new Set(allEcosystemDefaultVersionIds);
  for (const [key] of ecosystemByKey) {
    const lastUsedId = getLastUsedCheckpointIdForEcosystem(key);
    if (lastUsedId) ids.add(lastUsedId);
  }
  return [...ids];
}

/**
 * Returns the last-used model version ID for a given ecosystem key.
 * Reads from the ecosystem-scoped localStorage entry written by the storage adapter.
 * Returns undefined if no previous selection exists.
 */
export function getLastUsedCheckpointIdForEcosystem(ecosystemKey: string): number | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  const eco = ecosystemByKey.get(ecosystemKey);
  if (!eco) return undefined;

  const group = getEcosystemGroup(eco.id);
  const scopeKey = group ? group.id : eco.key;

  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}.ecosystem.${scopeKey}`);
    if (!stored) return undefined;
    const values = JSON.parse(stored) as Record<string, unknown>;
    const modelId = (values?.model as { id?: unknown } | undefined)?.id;
    return typeof modelId === 'number' ? modelId : undefined;
  } catch {
    return undefined;
  }
}

// =============================================================================
// Storage Adapter
// =============================================================================

/**
 * Ecosystem keys whose cfgScale/steps should be scoped per model version.
 * Only ecosystems that ship a turbo/distilled variant with meaningfully
 * different slider ranges than their base variant — switching between
 * variants would otherwise clamp values one-way (e.g. base cfg=10 → turbo
 * cfg=2 → switch back keeps cfg=2).
 *
 * Add new ecosystems here when introducing a turbo/distilled variant.
 */
const TURBO_VARIANT_ECOSYSTEMS = new Set<string>([
  'Lens',
  'Ernie',
  'ZImageTurbo',
  'ZImageBase',
  'Boogu',
  'Krea2',
  'Anima',
  'MageFlow',
]);

const storageAdapter = createLocalStorageAdapter({
  prefix: STORAGE_KEY,
  groups: [
    // User preferences - persisted across resets
    { name: 'preferences', keys: ['outputFormat', 'priority'] },
    // Workflow is the primary selector - stored globally.
    // `snippets` (loaded wildcard sets + mode/batchCount) lives here too so
    // a user's loaded wildcard packs persist across ecosystem and workflow
    // switches — a pack loaded while on SDXL stays available on Flux.
    {
      keys: ['workflow', 'prompt', 'negativePrompt', 'seed', 'quantity', 'snippets', 'controlNets'],
    },
    { name: 'output', keys: ['ecosystem'], scope: 'output' },
    // ecosystem is scoped to workflow (different workflows may use different ecosystems)
    // { name: 'workflow', keys: ['ecosystem'], scope: 'workflow' },
    {
      name: 'workflow',
      keys: ['quantity'],
      scope: 'workflow',
      condition: (ctx) => ctx.workflow === 'txt2img:draft',
    },
    { name: 'workflow', keys: ['images', 'video'], scope: 'workflow' },
    // Ecosystem groups - settings scoped by group ID for grouped ecosystems
    // This allows settings to persist when switching between variants (e.g., Wan 2.5 <-> Wan 2.2)
    ...ecosystemGroups.map((group) => ({
      name: 'ecosystem',
      keys: '*' as const,
      scope: group.id,
      condition: (ctx: Record<string, unknown>) => {
        const ecoKey = ctx.ecosystem as string | undefined;
        if (!ecoKey) return false;
        const eco = ecosystemByKey.get(ecoKey);
        if (!eco) return false;
        return group.ecosystemIds.includes(eco.id);
      },
    })),
    // MiniMax H3's turbo is a toggle on one model version rather than a separate
    // version, so model.id alone doesn't separate the two step ranges (1-20 turbo
    // vs 10-60 base) — scope on the toggle as well.
    {
      name: 'ecosystem',
      keys: ['steps'],
      scope: ['ecosystem', 'model.id', 'turbo'],
      condition: (ctx) => ctx.ecosystem === 'MiniMaxH3' && ctx.turbo !== undefined,
    },
    // cfgScale + steps are scoped per model version for ecosystems that ship
    // a turbo (or otherwise distilled) variant alongside a base variant, where
    // the two have meaningfully different slider ranges. Without this, turbo's
    // tight ranges and base's wide ones trample each other on switch.
    //   - Lens normal (cfg 1-20, steps 1-50) vs turbo (cfg 1-2, steps 1-12)
    //   - Ernie base/turbo (same ranges, but different defaults)
    //   - ZImageBase (cfg 1-10, steps 1-50) vs ZImageTurbo (cfg 1-2, steps 1-15)
    // Conditional + non-wildcard so it joins the pre-claim phase and prevents
    // upstream wildcards from also writing duplicate copies to their storage.
    {
      name: 'ecosystem',
      keys: ['cfgScale', 'steps'],
      scope: ['ecosystem', 'model.id'],
      condition: (ctx) => {
        const model = ctx.model as { id?: number } | undefined;
        if (model?.id == null) return false;
        return TURBO_VARIANT_ECOSYSTEMS.has(ctx.ecosystem as string);
      },
    },
    // Model-family specific settings scoped to individual ecosystem (for standalone ecosystems)
    // Values for inactive nodes are automatically retained in storage
    // (e.g., cfgScale/steps when switching to Ultra mode which doesn't have them)
    { name: 'ecosystem', keys: '*', scope: 'ecosystem' },
    // Fallback for non-ecosystem workflows (upscale, interpolate, etc.)
    // These workflows don't have ecosystem, so the above group is skipped
    // and this catch-all handles remaining keys at workflow scope
    { name: 'workflow', keys: '*', scope: 'workflow' },
  ],
});

// =============================================================================
// Types
// =============================================================================

export interface GenerationFormProviderProps {
  children: ReactNode;
  /** Default/initial values to pass to graph.init() (e.g., from API data) */
  defaultValues?: Record<string, unknown>;
  /** Enable debug mode for the graph */
  debug?: boolean;
  /** Skip loading values from localStorage (use only defaultValues and node defaults) */
  skipStorage?: boolean;
}

// =============================================================================
// Inner Component (has access to ResourceDataProvider context)
// =============================================================================

function InnerProvider({
  children,
  defaultValues,
  debug = false,
  skipStorage = false,
}: GenerationFormProviderProps) {
  const status = useGenerationStatus();
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();
  const { selfHostedDisabledEcosystems, selfHostedMode, gateRules } = useGenerationConfig();
  const { registerResourceId, unregisterResourceId } = useResourceDataContext();

  const isModerator = !!currentUser?.isModerator;

  // Build external context from generation status
  const externalContext = useMemo<GenerationCtx>(
    () => ({
      limits: {
        maxQuantity: status.limits.quantity,
        maxResources: status.limits.resources,
        vidQuantity: VID_QUANTITY_BY_TIER[status.tier] ?? 1,
      },
      user: {
        isMember: status.tier !== 'free' || isModerator,
        tier: status.tier,
      },
      flags: featureFlags,
      selfHostedDisabledEcosystems,
      selfHostedMode,
      gateRules,
    }),
    [
      status.limits.quantity,
      status.limits.resources,
      status.tier,
      isModerator,
      featureFlags,
      selfHostedDisabledEcosystems,
      selfHostedMode,
      gateRules,
    ]
  );

  // Initialize the DataGraph (clone, attach storage, init once)
  const { graph } = useDataGraph({
    graph: generationGraph,
    storage: storageAdapter,
    defaultValues,
    externalContext,
    debug,
    skipStorage,
  });

  // On mount: detect if the stored workflow/ecosystem was auto-corrected during init
  // (e.g., workflow or ecosystem was removed/disabled). Show compatibility modal so the user
  // can pick a valid ecosystem instead of being silently switched.
  useEffect(() => {
    try {
      // Read the raw stored workflow from localStorage (before graph corrections)
      const globalStored = localStorage.getItem(STORAGE_KEY);
      if (!globalStored) return;
      const globalValues = JSON.parse(globalStored) as Record<string, unknown>;
      const storedWorkflow = globalValues.workflow as string | undefined;
      if (!storedWorkflow) return;

      // 3D-model workflows pick their (resource-less) ecosystem explicitly via
      // the inline picker; there's no silent auto-correction to surface. They're
      // also output-type `model3d`, which the image/video heuristic below
      // misclassifies as `image` — that reads the wrong stored ecosystem scope,
      // sees a guaranteed mismatch vs the resolved 3D ecosystem, and (now that
      // img2model3d exposes multiple ecosystems) spuriously fires the
      // compatibility modal on panel open. Skip 3D entirely.
      if (workflowConfigByKey.get(storedWorkflow)?.category === 'model3d') return;

      // Determine the output type from the stored workflow key prefix
      // (can't use getOutputTypeForWorkflow — it falls back to 'image' for unknown workflows)
      const storedOutputType = storedWorkflow.includes('2vid') ? 'video' : 'image';

      // Read the stored ecosystem from the output-scoped storage
      const outputStored = localStorage.getItem(`${STORAGE_KEY}.output.${storedOutputType}`);
      if (!outputStored) return;
      const outputValues = JSON.parse(outputStored) as Record<string, unknown>;
      const storedEcosystem = outputValues.ecosystem as string | undefined;
      if (!storedEcosystem) return;

      // Resolve what the graph corrected to
      const snapshot = graph.getSnapshot() as Record<string, unknown>;
      const resolvedEcosystem = snapshot.ecosystem as string | undefined;

      // If the stored ecosystem matches the resolved one, no correction happened
      if (storedEcosystem === resolvedEcosystem) return;

      // Determine the target workflow — use stored if known, else derive from prefix
      const resolvedWorkflow = workflowConfigByKey.has(storedWorkflow)
        ? storedWorkflow
        : storedOutputType === 'video'
        ? 'txt2vid'
        : 'txt2img';

      // Verify the stored ecosystem is actually incompatible (not just an internal re-org)
      const storedEco = ecosystemByKey.get(storedEcosystem);
      if (storedEco && isWorkflowAvailable(resolvedWorkflow, storedEco.id)) return;

      // Workflows with exactly one ecosystem (e.g. img2img:upscale → Upscaler) have no
      // ambiguity — the graph auto-corrects to the only valid ecosystem, no modal needed.
      const workflowEcosystemIds = getEcosystemsForWorkflow(resolvedWorkflow);
      if (workflowEcosystemIds.length === 1) return;

      // Ecosystem was corrected — show compatibility modal
      openCompatibilityConfirmModal({
        pendingChange: buildWorkflowPendingChange({
          workflowId: resolvedWorkflow,
          currentEcosystem: storedEcosystem,
        }),
        onConfirm: (selectedEcosystemKey) => {
          if (selectedEcosystemKey) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            graph.set({ workflow: resolvedWorkflow, ecosystem: selectedEcosystemKey } as any);
          }
        },
      });
    } catch {
      // localStorage read failed, skip
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-domain handoff: a `?gen=...` param means another domain (typically
  // .com → .red via the yellow-buzz upsell) sent us its current form snapshot.
  // Decode it, push into the graph store as a remix, and strip the param so
  // refresh/back doesn't re-trigger. Runs before the applyStoreData effect
  // below so the queued data is picked up by its initial mount run.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const handoff = url.searchParams.get(GENERATION_HANDOFF_PARAM);
    if (!handoff) return;

    const decoded = decodeGenerationHandoff(handoff);

    url.searchParams.delete(GENERATION_HANDOFF_PARAM);
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);

    if (!decoded) return;

    generationGraphStore.setData({
      params: decoded.params,
      resources: decoded.resources,
      runType: 'remix',
    });
  }, []);

  // `/generate?modelVersionId=…` deep link. Scoped to the generate route on
  // purpose: model pages use the same param to pick a version, and the panel
  // mounts there too — without the guard, opening a model page would yank the
  // generator open. Yields to a `?gen=` handoff, which has already queued data
  // by the time this runs.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.location.pathname.startsWith('/generate')) return;

    const url = new URL(window.location.href);
    const raw = url.searchParams.get('modelVersionId');
    if (!raw) return;

    url.searchParams.delete('modelVersionId');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);

    if (useGenerationGraphStore.getState().data) return;

    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0) return;

    generationGraphPanel.open({ type: 'modelVersion', id });
  }, []);

  // Sync generation graph store data into the graph
  // - Remix/Replay: full override (reset + set)
  // - Run/Patch: partial update (set only)
  const prevCounterRef = useRef(0);
  useEffect(() => {
    function applyStoreData() {
      const { data, counter } = useGenerationGraphStore.getState();
      if (!data || counter === prevCounterRef.current) return;
      prevCounterRef.current = counter;

      // Params are already mapped via mapDataToGraphInput (workflow, ecosystem, aspectRatio, etc.)
      // Just need to split flat resources into model/resources/vae for graph nodes
      const split = splitResourcesByType(data.resources.map(toResourceData));

      if (data.runType === 'remix' || data.runType === 'replay') {
        // Exclude output settings from remixed params so they don't override current values
        const { quantity, priority, outputFormat, ...paramsWithoutOutputSettings } = data.params;

        // Resolve workflow — if missing or unrecognized, derive from ecosystem's base model type
        const remixEcosystemKey = paramsWithoutOutputSettings.ecosystem as string | undefined;
        const incomingWorkflow = paramsWithoutOutputSettings.workflow as string | undefined;

        // PolyGen short-circuit: detect a 3D-model remix from either the
        // ecosystem key or the workflow key, before any "unknown-workflow
        // fallback" can rewrite it to `txt2img`. If we're here it's
        // unambiguous which PolyGen workflow to land on (txt vs img), so
        // skip the fallback / compat-modal entirely and apply directly.
        // Mirrors how the workflow-config lookups treat PolyGen as a
        // first-class workflow, but bullet-proof against any downstream
        // resolution quirk that has previously surfaced the ecosystem
        // picker on remix.
        const isPolyGenRemix =
          (!!remixEcosystemKey && MODEL3D_ECOSYSTEM_KEYS.has(remixEcosystemKey)) ||
          incomingWorkflow === 'txt2model3d' ||
          incomingWorkflow === 'img2model3d';

        // The 3D ecosystem to land on — preserve the model (Meshy/Tripo/
        // Hunyuan3D) the source was generated with. Falls back to PolyGen
        // (Meshy) for legacy items whose metadata predates the model selector.
        const model3dEcosystem: 'PolyGen' | 'Tripo' | 'Hunyuan3D' =
          remixEcosystemKey && MODEL3D_ECOSYSTEM_KEYS.has(remixEcosystemKey)
            ? (remixEcosystemKey as 'PolyGen' | 'Tripo' | 'Hunyuan3D')
            : 'PolyGen';

        let resolvedWorkflow: string;
        if (isPolyGenRemix) {
          // 3D path: keep an incoming `txt2model3d` / `img2model3d` verbatim.
          // Tripo/Hunyuan3D are image-to-3D only; only PolyGen (Meshy) has a
          // text branch, so consult `process` for it — `multiImageTo3D` is
          // Meshy v7's multi-view operation and is equally image-driven. Falls
          // back to a sensible default so the user lands on the form even if
          // both fields were lost.
          resolvedWorkflow =
            incomingWorkflow === 'txt2model3d' || incomingWorkflow === 'img2model3d'
              ? incomingWorkflow
              : model3dEcosystem !== 'PolyGen' ||
                paramsWithoutOutputSettings.process === 'imageTo3D' ||
                paramsWithoutOutputSettings.process === 'multiImageTo3D'
              ? 'img2model3d'
              : 'txt2model3d';
        } else if (incomingWorkflow && workflowConfigByKey.has(incomingWorkflow)) {
          resolvedWorkflow = incomingWorkflow;
        } else {
          // Workflow unknown — infer output type from the workflow key prefix or ecosystem
          const isVideoWorkflow =
            incomingWorkflow?.includes('2vid') || incomingWorkflow?.startsWith('vid2');
          const remixEcoEntry = remixEcosystemKey
            ? ecosystemByKey.get(remixEcosystemKey)
            : undefined;
          const isVideoEco =
            remixEcoEntry &&
            getBaseModelsByEcosystemId(remixEcoEntry.id).some((m) =>
              Array.isArray(m.type) ? m.type.includes('video') : m.type === 'video'
            );
          resolvedWorkflow = isVideoWorkflow || isVideoEco ? 'txt2vid' : 'txt2img';
        }

        // Check if the remix ecosystem supports the resolved workflow
        const remixEco = remixEcosystemKey ? ecosystemByKey.get(remixEcosystemKey) : undefined;
        // PolyGen short-circuit: always treat the workflow as supported so
        // the modal can't fire while a 3D-model remix is in progress. The
        // user explicitly asked for the PolyGen graph and the saved
        // workflowMetadata already proves the combination is reachable.
        const ecosystemSupportsWorkflow = isPolyGenRemix
          ? true
          : remixEco
          ? isWorkflowAvailable(resolvedWorkflow, remixEco.id)
          : !remixEcosystemKey; // Unknown ecosystem key — treat as incompatible

        // Build the values to apply (shared between both paths). For a PolyGen
        // remix we explicitly pin the ecosystem in case the saved metadata
        // dropped it — the two-stage `graph.set` above relies on this being
        // present in the final values blob so a downstream re-evaluation
        // doesn't snap back to whatever the discriminator defaulted to.
        const remixValues = {
          ...paramsWithoutOutputSettings,
          ...(isPolyGenRemix ? { ecosystem: model3dEcosystem } : {}),
          workflow: resolvedWorkflow,
          model: split.model,
          upscaler: split.upscaler,
          resources: split.resources,
          vae: split.vae,
        };

        if (!ecosystemSupportsWorkflow && remixEcosystemKey) {
          // Ecosystem doesn't support this workflow — show modal before applying
          openCompatibilityConfirmModal({
            pendingChange: buildWorkflowPendingChange({
              workflowId: resolvedWorkflow,
              currentEcosystem: remixEcosystemKey,
            }),
            onConfirm: (selectedEcosystemKey) => {
              if (selectedEcosystemKey) {
                graph.reset({ exclude: ['quantity', 'priority', 'outputFormat'] });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                graph.set({ ...remixValues, ecosystem: selectedEcosystemKey } as any);
              }
            },
          });
        } else {
          // Compatible — apply immediately.
          graph.reset({ exclude: ['quantity', 'priority', 'outputFormat'] });
          // Each 3D ecosystem lives in its own discriminated subgraph (activates
          // only when `ecosystem` matches, e.g. `PolyGen`/`Tripo`/`Hunyuan3D`),
          // and its child nodes — `images`, `targetPolycount`, `texture`,
          // `enableRigging`, etc. — only exist while that subgraph is active. A
          // single `graph.set` with
          // the full blob can drop those child keys because they're
          // evaluated before the discriminator has switched, so the remix
          // lands the user on the default ecosystem with an empty form
          // (the user-reported "still asked for ecosystem" symptom).
          //
          // Two-stage set: switch the discriminator first (ecosystem +
          // workflow, which also drives the `process` transform), then
          // push the rest so every PolyGen-specific value lands in the
          // active subgraph.
          if (
            isPolyGenRemix &&
            (resolvedWorkflow === 'txt2model3d' || resolvedWorkflow === 'img2model3d')
          ) {
            graph.set({
              ecosystem: model3dEcosystem,
              workflow: resolvedWorkflow,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose superset of discriminated union
          graph.set(remixValues as any);
        }
      } else if (data.runType === 'wildcard') {
        // Add a wildcard set id to the snippets node, preserving the user's
        // existing mode/batchCount/targets/seed. Idempotent — adding an
        // already-loaded id is a no-op.
        const wildcardSetId = data.params.wildcardSetId as number | undefined;
        if (wildcardSetId != null) {
          const snapshot = graph.getSnapshot() as { snippets?: SnippetsNodeValue };
          const existing = snapshot.snippets;
          if (!existing?.wildcardSetIds.includes(wildcardSetId)) {
            const nextSnippets: SnippetsNodeValue = existing
              ? { ...existing, wildcardSetIds: [...existing.wildcardSetIds, wildcardSetId] }
              : { wildcardSetIds: [wildcardSetId], mode: 'random', batchCount: 1, targets: {} };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose superset of discriminated union
            graph.set({ snippets: nextSnippets } as any);
          }
        }
      } else if (data.runType === 'append') {
        // Append: merge incoming images with existing, dedup by URL, cap at max
        const snapshot = graph.getSnapshot() as Record<string, unknown>;
        let existingImages: Array<{ url: string }> = [];
        if (snapshot.workflow === data.params.workflow) {
          // Same workflow — use live snapshot images
          existingImages = ((snapshot.images ?? []) as Array<{ url: string }>) || [];
        } else {
          // Switching workflows — read persisted images for the target workflow from localStorage
          try {
            const stored = localStorage.getItem(`${STORAGE_KEY}.workflow.${data.params.workflow}`);
            if (stored) {
              const parsed = JSON.parse(stored);
              existingImages = (parsed.images as Array<{ url: string }>) || [];
            }
          } catch {
            // Invalid JSON, start fresh
          }
        }
        const incomingImages =
          ((data.params.images ?? []) as Array<{ url: string; width: number; height: number }>) ||
          [];

        // Deduplicate by URL
        const existingUrls = new Set(existingImages.map((img) => img.url));
        const newImages = incomingImages.filter((img) => !existingUrls.has(img.url));
        const mergedImages = [...existingImages, ...newImages];

        // Set workflow (in case we're switching to upscale) and merged images
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        graph.set({ workflow: data.params.workflow, images: mergedImages } as any);
      } else {
        // Run/Patch: model/vae overwrite, resources merge with existing
        const snapshot = graph.getSnapshot() as Record<string, unknown>;
        const existingResources = (snapshot.resources ?? []) as ResourceData[];
        const incomingIds = new Set(split.resources.map((r) => r.id));

        // Determine whether to preserve the current ecosystem or switch to the incoming one.
        // Preserve when ALL incoming resources are compatible (full or partial) with the current
        // ecosystem. Switch only when an incoming resource is truly incompatible.
        const currentEcosystem = snapshot.ecosystem as string | undefined;
        const currentEco = currentEcosystem ? ecosystemByKey.get(currentEcosystem) : undefined;
        const allCompatible = currentEco && areResourcesCompatible(currentEco.id, data.resources);

        const incomingEcosystem = data.params.ecosystem as string | undefined;

        // If current workflow doesn't support ecosystems (e.g. img2meta),
        // switch to the user's last used workflow that supports the resource's ecosystem
        const currentWorkflow = snapshot.workflow as string | undefined;
        const currentWorkflowConfig = currentWorkflow
          ? workflowConfigByKey.get(currentWorkflow)
          : undefined;
        let targetWorkflow = data.params.workflow as string | undefined;

        // Upscaler models always switch to the upscale workflow
        if (split.upscaler) {
          targetWorkflow = 'img2img:upscale';
        }

        if (
          !targetWorkflow &&
          currentWorkflowConfig &&
          currentWorkflowConfig.ecosystemIds.length === 0
        ) {
          // Determine the resource's ecosystem from the checkpoint (or first resource with baseModel)
          const resourceWithBaseModel =
            split.resources.find((r) => r.model.type === 'Checkpoint') ??
            split.resources.find((r) => r.baseModel);
          const resourceEcosystem = resourceWithBaseModel?.baseModel
            ? getEcosystem(resourceWithBaseModel.baseModel)
            : undefined;

          if (resourceEcosystem) {
            const lastUsed = workflowPreferences.getLastUsedWorkflowForEcosystem(
              resourceEcosystem.id
            );
            targetWorkflow = lastUsed?.workflow ?? 'txt2img';
          } else {
            const lastUsed = workflowPreferences.getLastUsedWorkflow();
            targetWorkflow = lastUsed?.workflow ?? 'txt2img';
          }
        }

        // When no current ecosystem is available (not in graph, not in localStorage) and
        // the incoming resources are compatible with multiple ecosystems, show the
        // compatibility modal so the user can choose rather than silently picking one.
        if (!currentEco && !allCompatible) {
          const resolvedWorkflow = targetWorkflow ?? currentWorkflow ?? 'txt2img';
          const workflowEcosystemIds = getEcosystemsForWorkflow(resolvedWorkflow);

          // Filter to ecosystems that are compatible with ALL incoming resources
          const compatibleEcosystemIds = workflowEcosystemIds.filter((ecoId) =>
            areResourcesCompatible(ecoId, data.resources)
          );

          if (compatibleEcosystemIds.length > 1) {
            // Multiple compatible ecosystems — let the user choose.
            // Default to the most recently used ecosystem if it's in the compatible list.
            const lastUsed = workflowPreferences.getLastUsedWorkflow();
            const lastUsedEco = lastUsed?.ecosystem
              ? ecosystemByKey.get(lastUsed.ecosystem)
              : undefined;
            const lastUsedIsCompatible =
              lastUsedEco && compatibleEcosystemIds.includes(lastUsedEco.id);

            const resolvedDefault =
              (lastUsedIsCompatible ? lastUsed!.ecosystem : undefined) ??
              (compatibleEcosystemIds[0]
                ? ecosystemById.get(compatibleEcosystemIds[0])?.key
                : undefined) ??
              '';

            openCompatibilityConfirmModal({
              pendingChange: {
                type: 'workflow',
                value: resolvedWorkflow,
                optionId: resolvedWorkflow,
                currentEcosystem: incomingEcosystem ?? '',
                compatibleEcosystemIds,
                defaultEcosystemKey: resolvedDefault,
              },
              onConfirm: (selectedEcosystemKey) => {
                if (selectedEcosystemKey) {
                  // Step 1: Switch workflow and ecosystem so the graph restores stored
                  // resources via the storage adapter's valueProvider (on img2meta the
                  // snapshot has no resources — they live in localStorage).
                  const { ecosystem: _, ...restParams } = data.params;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  graph.set({
                    ...restParams,
                    ecosystem: selectedEcosystemKey,
                    workflow: resolvedWorkflow,
                  } as any);

                  // Step 2: Read the restored resources and merge incoming on top.
                  const restored = graph.getSnapshot() as Record<string, unknown>;
                  const restoredResources = (restored.resources ?? []) as ResourceData[];
                  const selectedEco = ecosystemByKey.get(selectedEcosystemKey);
                  const filtered = selectedEco
                    ? filterCompatibleResources(selectedEco.id, restoredResources, incomingIds)
                    : restoredResources.filter((r) => !incomingIds.has(r.id));
                  const merged = [...filtered, ...split.resources];
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  graph.set({
                    resources: merged,
                    ...(split.model && { model: split.model }),
                    ...(split.vae && { vae: split.vae }),
                  } as any);
                }
              },
            });
            generationGraphStore.clearData();
            return;
          }
        }

        // Use the effective ecosystem (current if preserved, incoming if switching) to filter
        // existing resources. This prevents removing compatible resources when the ecosystem
        // isn't actually changing (e.g. adding a cross-ecosystem TextualInversion).
        const effectiveEcosystemKey = allCompatible ? currentEcosystem : incomingEcosystem;
        const effectiveEcosystem = effectiveEcosystemKey
          ? ecosystemByKey.get(effectiveEcosystemKey)
          : undefined;

        // Filter existing resources: keep only those compatible with the effective ecosystem
        const compatibleExisting = effectiveEcosystem
          ? filterCompatibleResources(effectiveEcosystem.id, existingResources, incomingIds)
          : existingResources.filter((r) => !incomingIds.has(r.id));

        const mergedResources = [...compatibleExisting, ...split.resources];

        const { ecosystem: _incomingEco, ...paramsWithoutEcosystem } = data.params;
        const values = {
          ...(allCompatible ? paramsWithoutEcosystem : data.params),
          ...(targetWorkflow && { workflow: targetWorkflow }),
          resources: mergedResources,
          ...(split.model && { model: split.model }),
          ...(split.upscaler && { upscaler: split.upscaler }),
          ...(split.vae && { vae: split.vae }),
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- loose superset of discriminated union
        graph.set(values as any);
      }

      generationGraphStore.clearData();
    }

    // Check on mount (data may arrive before component mounts)
    applyStoreData();

    // Subscribe to future changes
    return useGenerationGraphStore.subscribe(applyStoreData);
  }, [graph]);

  // Register resource IDs from graph for hydration
  // This extracts IDs from model/resources/vae nodes and registers them with ResourceDataProvider.
  // Components can then access full resource data via getResourceData(id) without managing hydration.
  const registeredIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    function syncResourceIds() {
      const snapshot = graph.getSnapshot() as Record<string, unknown>;

      // Extract resource values from known resource nodes
      const resourceValues: PartialResourceValue[] = [];
      if (snapshot.model) resourceValues.push(snapshot.model as PartialResourceValue);
      if (snapshot.vae) resourceValues.push(snapshot.vae as PartialResourceValue);
      if (Array.isArray(snapshot.resources)) {
        resourceValues.push(...(snapshot.resources as PartialResourceValue[]));
      }

      // Find IDs that need hydration (missing full data)
      const idsToRegister = new Set(
        resourceValues.filter((v) => v?.id && needsHydration(v)).map((v) => v.id)
      );

      // Unregister IDs that are no longer needed
      for (const id of registeredIdsRef.current) {
        if (!idsToRegister.has(id)) {
          unregisterResourceId(id);
          registeredIdsRef.current.delete(id);
        }
      }

      // Register new IDs
      for (const id of idsToRegister) {
        if (!registeredIdsRef.current.has(id)) {
          registerResourceId(id);
          registeredIdsRef.current.add(id);
        }
      }
    }

    // Initial sync
    syncResourceIds();

    // Subscribe to graph changes
    return graph.subscribe(syncResourceIds);
  }, [graph, registerResourceId, unregisterResourceId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const id of registeredIdsRef.current) {
        unregisterResourceId(id);
      }
      registeredIdsRef.current.clear();
    };
  }, [unregisterResourceId]);

  // Mirror the live graph snapshot into a module-level cache so cross-domain
  // handoff links rendered outside the DataGraphProvider subtree (e.g. QueueItem
  // CTAs on mobile where the form tab is unmounted) can still read the user's
  // current form state. Intentionally does NOT clear on unmount — the cache
  // should survive tab switches.
  useEffect(() => {
    function sync() {
      setGenerationSnapshotCache({
        snapshot: graph.getSnapshot() as Record<string, unknown>,
        computedKeys: graph.getComputedKeys(),
      });
    }
    sync();
    return graph.subscribe(sync);
  }, [graph]);

  // Track preferred ecosystem when baseModel changes
  // This keeps the workflow preferences store in sync with what the user is actually using
  useEffect(() => {
    function syncPreferredEcosystem() {
      const snapshot = graph.getSnapshot() as Record<string, unknown>;
      const ecosystem = snapshot.ecosystem as string | undefined;
      const workflow = snapshot.workflow as string | undefined;
      if (!ecosystem || !workflow) return;

      // Update the preferred ecosystem for this specific workflow
      workflowPreferences.setPreferredEcosystem(workflow, ecosystem);
    }

    // Subscribe to graph changes
    return graph.subscribe(syncPreferredEcosystem);
  }, [graph]);

  // Enforce version constraints when workflow changes.
  // If the current model is excluded by the workflow's config (excludeModelVersionIds),
  // force-switch to the first valid version. This handles ALL entry points (form, store,
  // modal, generated-item menu) since it reacts to graph state, not the source of change.
  useEffect(() => {
    let prevWorkflow: string | undefined;
    return graph.subscribe(() => {
      const snapshot = graph.getSnapshot() as Record<string, unknown>;
      const workflow = snapshot.workflow as string | undefined;
      if (!workflow || workflow === prevWorkflow) return;
      prevWorkflow = workflow;

      const config = workflowConfigByKey.get(workflow);
      if (!config?.excludeModelVersionIds?.length) return;

      const currentModelId = (snapshot.model as { id?: number } | undefined)?.id;
      if (!currentModelId || !config.excludeModelVersionIds.includes(currentModelId)) return;

      // Current model is excluded — read version options from model meta and pick first valid one
      const modelMeta = graph.getNodeMeta('model' as never) as
        | { versions?: VersionGroup }
        | undefined;
      if (!modelMeta?.versions) return;

      const allIds = getAllVersionIds(modelMeta.versions);
      const validId = [...allIds].find((id) => !config.excludeModelVersionIds!.includes(id));
      if (validId) {
        // Defer to avoid re-entrant graph.set during notification callback.
        // queueMicrotask runs before the next paint, so React batches both updates.
        queueMicrotask(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          graph.set({ model: { id: validId, model: { type: 'Checkpoint' } } } as any);
        });
      }
    });
  }, [graph]);

  return (
    <DataGraphProvider graph={graph}>
      <WhatIfProvider>{children}</WhatIfProvider>
    </DataGraphProvider>
  );
}

// =============================================================================
// Component
// =============================================================================

export function GenerationFormProvider({
  children,
  defaultValues,
  debug = false,
  skipStorage = false,
}: GenerationFormProviderProps) {
  return (
    <InnerProvider defaultValues={defaultValues} debug={debug} skipStorage={skipStorage}>
      <ExperimentalRulesSync />
      {children}
    </InnerProvider>
  );
}

/** Convert GenerationResource to ResourceData (matching data-graph resourceSchema).
 * Resources arriving from the cross-domain handoff or storage may be partially
 * hydrated — `trainedWords` and `model.type` can be missing — so guard both. */
function toResourceData(r: GenerationResource): ResourceData {
  if (r.epochDetails) return r; // Shouldn't need to get fresh data for resources with epochDetails since they have all necessary info for compatibility checks (type, baseModel, epochNumber) and aren't selectable in the UI
  return {
    id: r.id,
    baseModel: r.baseModel,
    model: { type: r.model?.type as ResourceData['model']['type'] },
    strength: r.strength,
    trainedWords: r.trainedWords && r.trainedWords.length > 0 ? r.trainedWords : undefined,
  };
}
